import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { ComponentsModule } from '../../components/components.module';
import { HotelService } from '../../core/services/hotel.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { CountryService, Country, Region } from '../../core/services/country.service';
import {
  OnboardingRequestService,
  OnboardingRequest,
} from '../../core/services/onboarding-request.service';
import { EventService } from '../../core/services/event.service';
import { CreateHotelRequest } from '../../core/interfaces/hotel';
import { RadioOption } from '../../components/ui/input/radio/radio.component';


@Component({
  selector: 'app-hotel-creation',
  templateUrl: './hotel-creation.component.html',
  styleUrls: ['./hotel-creation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NgSelectModule,
    ComponentsModule,
  ],
})
export class HotelCreationComponent implements OnInit, OnDestroy {
  hotelForm!: FormGroup;
  submitted = false;
  isSubmitting = false;

  private readonly API_FIELD_TO_FORM_CONTROL: Record<string, string> = {
    email: 'email',
    phone: 'phone_number',
    booking_email: 'booking_email',
  };

  private readonly API_FIELD_TO_ERROR_KEY: Record<string, string> = {
    email: 'hotel_creation.server_errors.email',
    phone: 'hotel_creation.server_errors.phone',
    booking_email: 'hotel_creation.server_errors.booking_email',
  };

  countries: Country[] = [];
  regions: Region[] = [];
  ratingOptions: { value: number | string; label: string }[] = [];
  ratingDropdownOptions: { value: string; label: string }[] = [];
  currencyOptions: RadioOption[] = [
    { value: 'CHF', label: 'CHF' },
    { value: 'EUR', label: 'EUR' }
  ];
  salutationOptions: RadioOption[] = [];

  onboardingRequestId: number | null = null;
  prefillRequest: OnboardingRequest | null = null;

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private hotelService: HotelService,
    private countryService: CountryService,
    private onboardingRequestService: OnboardingRequestService,
    private eventService: EventService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.buildTranslatedRadioOptions();
    this.initializeForm();
    this.loadCountries();

    const requestId = this.route.snapshot.queryParamMap.get('onboardingRequestId');
    if (requestId) {
      this.onboardingRequestId = Number(requestId);
      this.loadOnboardingRequest(this.onboardingRequestId);
    }

    ['email', 'phone_number', 'booking_email'].forEach(fieldName => {
      this.subscriptions.add(
        this.hotelForm.get(fieldName)!.valueChanges.subscribe(() => {
          const control = this.hotelForm.get(fieldName)!;
          if (control.errors?.['serverError']) {
            const errors = { ...control.errors };
            delete errors['serverError'];
            control.setErrors(Object.keys(errors).length ? errors : null);
          }
        })
      );
    });

    this.subscriptions.add(
      this.hotelForm.get('country_id')!.valueChanges.subscribe(countryId => {
        this.hotelForm.get('region_id')!.setValue(null);
        this.hotelForm.get('region_id')!.clearValidators();
        this.hotelForm.get('region_id')!.updateValueAndValidity();
        this.regions = [];
        if (countryId) {
          this.loadRegions(countryId);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get f(): FormGroup['controls'] {
    return this.hotelForm.controls;
  }

  getError(field: string): string {
    const control = this.f[field];
    if (!control?.invalid || !control?.touched) return '';
    if (control.errors?.['serverError']) return control.errors['serverError'];
    if (control.errors?.['required']) return this.translate.instant('validations.' + field);
    if (control.errors?.['email']) return this.translate.instant('validations.email');
    return this.translate.instant('validations.' + field);
  }

  private buildTranslatedRadioOptions(): void {
    // translate.get() waits for translations to load before emitting,
    // unlike translate.instant() which returns the raw key if called too early.
    this.translate.get([
      'contact_info.mr', 'contact_info.ms',
      'hotel_info.rating_1_star', 'hotel_info.rating_2_stars', 'hotel_info.rating_3_stars',
      'hotel_info.rating_4_stars', 'hotel_info.rating_5_stars',
      'hotel_info.rating_swiss_lodge', 'hotel_info.rating_no_rating'
    ]).pipe(take(1)).subscribe(t => {
      this.salutationOptions = [
        { value: 'm', label: t['contact_info.mr'] },
        { value: 'f', label: t['contact_info.ms'] }
      ];
      this.ratingOptions = [
        { value: 1, label: t['hotel_info.rating_1_star'] },
        { value: 2, label: t['hotel_info.rating_2_stars'] },
        { value: 3, label: t['hotel_info.rating_3_stars'] },
        { value: 4, label: t['hotel_info.rating_4_stars'] },
        { value: 5, label: t['hotel_info.rating_5_stars'] },
        { value: 6, label: t['hotel_info.rating_swiss_lodge'] },
        { value: 'no_rating', label: t['hotel_info.rating_no_rating'] }
      ];
      this.ratingDropdownOptions = this.ratingOptions.map(o => ({
        value: String(o.value),
        label: o.label
      }));
      this.cdr.markForCheck();
    });
  }

  private initializeForm(): void {
    this.hotelForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      rating: [null, Validators.required],
      superior_hotel: [false],
      country_id: [null, Validators.required],
      currency: ['CHF', Validators.required],
      city: ['', [Validators.required, Validators.maxLength(255)]],
      postal_code: ['', [Validators.required, Validators.maxLength(255)]],
      street: [''],
      number: [''],
      region_id: [null, Validators.required],
      salutation: ['m', Validators.required],
      first_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
      last_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      phone_number: ['', [Validators.required, Validators.maxLength(30)]],
      hotel_phone_number: ['', [Validators.required, Validators.maxLength(30)]],
      booking_email: ['', [Validators.required, Validators.email, Validators.maxLength(150)]],
      role: [''],
      website: [''],
      general_email: ['', [Validators.email, Validators.maxLength(150)]],
    });
  }

  private loadCountries(): void {
    this.subscriptions.add(
      this.countryService.getAllCountries().subscribe({
        next: (countries) => {
          this.countries = countries ?? [];
          this.cdr.markForCheck();
        },
      })
    );
  }

  private loadRegions(countryId: number): void {
    this.subscriptions.add(
      this.countryService.getRegions(countryId).subscribe({
        next: (regions) => {
          this.regions = regions;
          if (regions.length > 0) {
            this.hotelForm.get('region_id')!.setValidators(Validators.required);
            this.hotelForm.get('region_id')!.updateValueAndValidity();
          }
          this.cdr.markForCheck();
        },
      })
    );
  }

  private loadOnboardingRequest(requestId: number): void {
    this.subscriptions.add(
      this.onboardingRequestService.getRequest(requestId).subscribe({
        next: (response) => {
          this.prefillRequest = response.request;
          this.prefillFormFromRequest(response.request);
          this.cdr.markForCheck();
        },
      })
    );
  }

  private prefillFormFromRequest(request: OnboardingRequest): void {
    const nameParts = (request.contact_name || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    this.hotelForm.patchValue({
      name: request.hotel_name || '',
      city: request.hotel_city || '',
      website: request.hotel_website || '',
      first_name: firstName,
      last_name: lastName,
      email: request.contact_email || '',
      booking_email: request.contact_email || '',
      phone_number: request.contact_phone || '',
      hotel_phone_number: request.contact_phone || '',
      role: this.formatContactPosition(request.contact_position),
      country_id: request.country_id || null
    });
  }

  /**
   * Converts a contact_position slug (e.g. "general_manager") to its
   * translated label (e.g. "General Manager") using the existing i18n keys.
   * Falls back to the raw value if the slug has no matching translation.
   */
  private formatContactPosition(position: string | null): string {
    if (!position) return '';
    const key = `ONBOARDING_REQUEST.POSITION_OPTIONS.${position.toUpperCase()}`;
    const translated = this.translate.instant(key);
    // translate.instant returns the key itself when no translation is found
    return translated !== key ? translated : position;
  }

  onSubmit(): void {
    this.submitted = true;
    this.hotelForm.markAllAsTouched();
    this.cdr.markForCheck();

    if (!this.hotelForm.valid) {
      return;
    }

    this.isSubmitting = true;
    this.eventService.broadcast('globalLoader:show', true);
    this.cdr.markForCheck();

    const payload = this.buildPayload();

    this.subscriptions.add(
      this.hotelService.createHotel(payload).subscribe({
        next: ({ message, hotelId }) => {
          this.isSubmitting = false;
          this.eventService.broadcast('globalLoader:hide', false);
          this.confirmationService.swalSuccess(message);
          this.navigateAfterCreation(hotelId, message);
        },
        error: (err) => {
          this.isSubmitting = false;
          this.eventService.broadcast('globalLoader:hide', false);

          if (err.errors && !Array.isArray(err.errors)) {
            this.applyServerErrors(err.errors);
          } else {
            this.confirmationService.swalError(this.translate.instant('notifications.error.save_failed'));
          }

          this.cdr.markForCheck();
        },
      })
    );
  }

  private applyServerErrors(errors: Record<string, string[]>): void {
    for (const [apiField, messages] of Object.entries(errors)) {
      const formField = this.API_FIELD_TO_FORM_CONTROL[apiField] ?? apiField;
      const control = this.hotelForm.get(formField);

      if (control && messages.length > 0) {
        const translationKey = this.API_FIELD_TO_ERROR_KEY[apiField];
        const errorMessage = translationKey
          ? this.translate.instant(translationKey)
          : messages[0];

        control.setErrors({ serverError: errorMessage });
        control.markAsTouched();
      }
    }
  }

  private navigateAfterCreation(hotelId: number | null, successMessage: string): void {
    if (this.onboardingRequestId) {
      this.onboardingRequestService.updateStatus(this.onboardingRequestId, 'completed').subscribe();
      this.router.navigate(['/onboarding-requests', this.onboardingRequestId], {
        state: { hotelCreatedSuccess: true, successMessage }
      });
      return;
    }

    if (hotelId) {
      this.hotelService.initializeSelectedHotel(hotelId.toString()).subscribe({
        next: () => this.router.navigate(['/dashboard']),
        error: () => this.router.navigate(['/hotels'])
      });
    } else {
      this.router.navigate(['/hotels']);
    }
  }

  private buildPayload(): CreateHotelRequest {
    const formValue = this.hotelForm.getRawValue();

    const payload: CreateHotelRequest = {
      name: formValue.name,
      rating: formValue.rating,
      country_id: formValue.country_id,
      currency: formValue.currency,
      city: formValue.city,
      zip: formValue.postal_code,
      salutation: formValue.salutation,
      first_name: formValue.first_name,
      last_name: formValue.last_name,
      email: formValue.email,
      phone: formValue.phone_number,
      hotel_phone_number: formValue.hotel_phone_number,
      booking_email: formValue.booking_email,
    };

    if (formValue.region_id) payload.region_id = formValue.region_id;
    payload.superior_hotel = formValue.superior_hotel ? 1 : 0;
    if (formValue.street) payload.street = formValue.street;
    if (formValue.number) payload.number = formValue.number;
    if (formValue.role) payload.role = formValue.role;
    if (formValue.website) payload.website = formValue.website;
    if (formValue.general_email) payload.general_email = formValue.general_email;
    if (this.onboardingRequestId) payload.onboarding_request_id = this.onboardingRequestId;

    return payload;
  }
}
